import { useState } from 'react';
import { Pencil, Trash2, Check, X, Plus, Github, Globe } from 'lucide-react';
import type { Project } from '../types';

interface EditableProjectCardProps {
  project?: Project;
  isAddNew?: boolean;
  isEditMode?: boolean;
  onSave: (project: Project) => void;
  onDelete?: (id: string) => void;
  onCancel?: () => void;
}

export default function EditableProjectCard({ 
  project, 
  isAddNew = false, 
  isEditMode = false,
  onSave, 
  onDelete, 
  onCancel 
}: EditableProjectCardProps) {
  const [isEditing, setIsEditing] = useState(isAddNew);
  const [formData, setFormData] = useState<Project>(
    project || { id: '', title: '', subtitle: '', description: '', tags: [] }
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [tagInput, setTagInput] = useState(project?.tags.join(', ') || '');

  const isValidUrl = (url: string): boolean => {
    if (!url) return true;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Name is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (formData.repo_url && !isValidUrl(formData.repo_url)) {
      newErrors.repo_url = 'Please enter a valid GitHub URL';
    }
    if (formData.website_url && !isValidUrl(formData.website_url)) {
      newErrors.website_url = 'Please enter a valid website URL';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    
    const tags = tagInput.split(',').map(t => t.trim()).filter(t => t);
    
    const projectData: Project = {
      ...formData,
      id: project?.id || Date.now().toString(),
      title: formData.title.trim(),
      subtitle: formData.subtitle.trim(),
      description: formData.description.trim(),
      tags,
      repo_url: formData.repo_url?.trim() || undefined,
      website_url: formData.website_url?.trim() || undefined,
    };
    
    onSave(projectData);
    setIsEditing(false);
    if (isAddNew) {
      setFormData({ id: '', title: '', subtitle: '', description: '', tags: [] });
      setTagInput('');
    }
  };

  const handleDelete = () => {
    if (project && onDelete && window.confirm('Delete this project?')) {
      onDelete(project.id);
    }
  };

  const handleCancel = () => {
    if (isAddNew) {
      onCancel?.();
    } else {
      setFormData(project || { id: '', title: '', subtitle: '', description: '', tags: [] });
      setTagInput(project?.tags.join(', ') || '');
      setIsEditing(false);
      setErrors({});
    }
  };

  if (isEditing) {
    return (
      <article className="card editing">
        <div className="card-dots">
          <span className="card-dot" />
          <span className="card-dot" />
          <span className="card-dot" />
        </div>
        <div className="card-content">
          <div className="edit-form">
            <div className="form-field">
              <input
                type="text"
                placeholder="Project Name *"
                value={formData.title}
                onChange={(e) => {
                  setFormData({ ...formData, title: e.target.value });
                  if (errors.title) setErrors({ ...errors, title: '' });
                }}
                className={errors.title ? 'error' : ''}
              />
              {errors.title && <span className="field-error">{errors.title}</span>}
            </div>
            
            <input
              type="text"
              placeholder="Subtitle (optional)"
              value={formData.subtitle}
              onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
            />
            
            <div className="form-field">
              <textarea
                placeholder="Description *"
                value={formData.description}
                onChange={(e) => {
                  setFormData({ ...formData, description: e.target.value });
                  if (errors.description) setErrors({ ...errors, description: '' });
                }}
                className={errors.description ? 'error' : ''}
                rows={2}
              />
              {errors.description && <span className="field-error">{errors.description}</span>}
            </div>
            
            <input
              type="text"
              placeholder="Tags (comma separated)"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
            />
            
            <div className="form-field">
              <div className="input-with-icon">
                <Github size={16} className="input-icon" />
                <input
                  type="url"
                  placeholder="GitHub Repository URL (optional)"
                  value={formData.repo_url || ''}
                  onChange={(e) => {
                    setFormData({ ...formData, repo_url: e.target.value });
                    if (errors.repo_url) setErrors({ ...errors, repo_url: '' });
                  }}
                  className={errors.repo_url ? 'error' : ''}
                />
              </div>
              {errors.repo_url && <span className="field-error">{errors.repo_url}</span>}
            </div>
            
            <div className="form-field">
              <div className="input-with-icon">
                <Globe size={16} className="input-icon" />
                <input
                  type="url"
                  placeholder="Website URL (optional)"
                  value={formData.website_url || ''}
                  onChange={(e) => {
                    setFormData({ ...formData, website_url: e.target.value });
                    if (errors.website_url) setErrors({ ...errors, website_url: '' });
                  }}
                  className={errors.website_url ? 'error' : ''}
                />
              </div>
              {errors.website_url && <span className="field-error">{errors.website_url}</span>}
            </div>
            
            <div className="edit-actions">
              <button onClick={handleSave} className="btn-save">
                <Check size={16} /> Save
              </button>
              <button onClick={handleCancel} className="btn-cancel">
                <X size={16} /> Cancel
              </button>
            </div>
          </div>
        </div>
      </article>
    );
  }

  if (!project) return null;

  return (
    <article className="card">
      <div className="card-dots">
        <span className="card-dot" />
        <span className="card-dot" />
        <span className="card-dot" />
      </div>
      <div className="card-content">
        {isEditMode && (
          <div className="edit-controls">
            <button onClick={() => setIsEditing(true)} className="btn-icon-edit" title="Edit">
              <Pencil size={14} />
            </button>
            <button onClick={handleDelete} className="btn-icon-delete" title="Delete">
              <Trash2 size={14} />
            </button>
          </div>
        )}
        <h3 className="card-title">{project.title}</h3>
        <p className="card-subtitle">{project.subtitle}</p>
        <p className="card-description">{project.description}</p>
        
        {project.tags.length > 0 && (
          <div className="tags">
            {project.tags.map(tag => (
              <span key={`${project.id}-${tag}`} className="tag">{tag}</span>
            ))}
          </div>
        )}
        
        <div className="project-links">
          {project.repo_url && (
            <a href={project.repo_url} target="_blank" rel="noopener noreferrer" className="card-link">
              <Github size={16} />
              <span>Repository</span>
            </a>
          )}
          {project.website_url && (
            <a href={project.website_url} target="_blank" rel="noopener noreferrer" className="card-link">
              <Globe size={16} />
              <span>Website</span>
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

// Add new project button/card
export function AddProjectButton({ onClick }: { onClick: () => void }) {
  return (
    <article className="card add-new-card" onClick={onClick}>
      <div className="card-dots">
        <span className="card-dot" />
        <span className="card-dot" />
        <span className="card-dot" />
      </div>
      <div className="card-content">
        <div className="add-new-content">
          <Plus size={32} />
          <span>Add New Project</span>
        </div>
      </div>
    </article>
  );
}
